import React, { useState, useEffect } from 'react';
import { supabase } from '../../../utils/supabase';
import LeaderboardWidget from '../../../components/Leaderboards/LeaderboardWidget';
import Icon from '../../../components/AppIcon';

/**
 * GameLeaderboards Component
 * Displays all leaderboards for a specific game
 *
 * Usage in game detail page:
 * <GameLeaderboards gameId={game.id} />
 */
export default function GameLeaderboards({ gameId }) {
  const [leaderboards, setLeaderboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLeaderboard, setSelectedLeaderboard] = useState(null);

  useEffect(() => {
    if (gameId) {
      loadLeaderboards();
    }
  }, [gameId]);

  const loadLeaderboards = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all active leaderboards for this game
      const { data, error: leaderboardsError } = await supabase
        .from('leaderboards')
        .select('id, leaderboard_key, name, description, scope, sort_order')
        .eq('game_id', gameId)
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      if (leaderboardsError) throw leaderboardsError;

      setLeaderboards(data || []);

      // Select first leaderboard by default
      if (data && data.length > 0 && !selectedLeaderboard) {
        setSelectedLeaderboard(data[0]);
      }

    } catch (err) {
      console.error('Error loading leaderboards:', err);
      setError('Failed to load leaderboards');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Loading leaderboards...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
            <p className="text-red-500">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (leaderboards.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8">
        <div className="text-center">
          <Icon name="Trophy" size={64} className="text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Leaderboards Yet</h3>
          <p className="text-sm text-muted-foreground">
            This game doesn't have any leaderboards configured yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Leaderboard selector (if multiple) */}
      {leaderboards.length > 1 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="ListFilter" size={18} className="text-muted-foreground" />
            <h4 className="font-semibold text-sm text-foreground">Select Leaderboard</h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {leaderboards.map(leaderboard => (
              <button
                key={leaderboard.id}
                onClick={() => setSelectedLeaderboard(leaderboard)}
                className={`p-3 rounded-lg border-2 transition-all text-left ${
                  selectedLeaderboard?.id === leaderboard.id
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 bg-card'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon
                    name={leaderboard.sort_order === 'desc' ? 'TrendingUp' : 'Timer'}
                    size={16}
                    className={selectedLeaderboard?.id === leaderboard.id ? 'text-primary' : 'text-muted-foreground'}
                  />
                  <h5 className={`font-semibold text-sm ${
                    selectedLeaderboard?.id === leaderboard.id ? 'text-primary' : 'text-foreground'
                  }`}>
                    {leaderboard.name}
                  </h5>
                </div>
                {leaderboard.description && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {leaderboard.description}
                  </p>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Selected leaderboard */}
      {selectedLeaderboard && (
        <div>
          {leaderboards.length === 1 && (
            <div className="mb-4">
              <h3 className="text-xl font-bold text-foreground mb-1">{selectedLeaderboard.name}</h3>
              {selectedLeaderboard.description && (
                <p className="text-sm text-muted-foreground">{selectedLeaderboard.description}</p>
              )}
            </div>
          )}

          <LeaderboardWidget
            gameId={gameId}
            leaderboardKey={selectedLeaderboard.leaderboard_key}
            scope={selectedLeaderboard.scope}
            limit={20}
            showUserRank={true}
          />
        </div>
      )}
    </div>
  );
}
